import React, { ReactElement, useEffect, useCallback, useMemo } from "react";
import { OperationalTargetsCarouselProps, OperationalTargetsCarouselStateProps } from "./OperationalTargetsCarousel.types";
import { OperationalTarget } from "../OperationalTarget";
import { useDispatch, useSelector } from "react-redux";
import { queryOperationalTargets } from "src/redux";

/**
 * OperationalTargetsCarousel component
 *
 * @param {OperationalTargetsCarouselProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function OperationalTargetsCarousel({
}: OperationalTargetsCarouselProps): ReactElement | null {
    const dispatch = useDispatch();
    const {
        items: operationalTargets,
        hasLoaded,
        isLoading,
    } = useSelector<Spintr.AppState, OperationalTargetsCarouselStateProps>(
        (state) => state.qualityHub.operationalTargets
    );

    useEffect(() => {
        if (hasLoaded || isLoading) {
            return;
        }

        dispatch(queryOperationalTargets());
    }, [hasLoaded, isLoading, dispatch]);

    // Make sure that we are currently in the window for the operational targets
    const targetsToRender = useMemo(() => operationalTargets.filter(target => {
        const now = new Date();
        const startDate = new Date(target.startDate);
        const endDate = target.endDate ? new Date(target.endDate) : null;

        if (startDate > now) {
            return false;
        }

        if (endDate && endDate < now) {
            return false;
        }

        return true;
    }), [operationalTargets]);

    const renderOpsTarget = useCallback((target: Spintr.OperationalTarget) => {
        return (
            <div key={target.id} className="OperationalTargetCard">
                <OperationalTarget target={target} />
            </div>
        );
    }, []);

    if (targetsToRender.length === 0) {
        return null;
    }

    return (
        <div className="OperationalTargetsCarousel">
            {targetsToRender.map(renderOpsTarget)}
        </div>
    );
}

export default OperationalTargetsCarousel;

